import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import { Alert } from "@/components/ui/Alert";
import { Dialog } from "@/components/ui/Dialog";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { deploymentsKeys, useUpdateRelease } from "../hooks/useDeployments";
import { isReleaseHalted } from "../lib/halt-history";
import type { Package, PackageInfo } from "../api/deployments.api";

interface EditReleaseDialogProps {
  appName: string;
  deploymentName: string;
  release: Package;
  autoHalted: boolean;
  open: boolean;
  onClose: () => void;
}

const isUnfinishedRollout = (rollout: Package["rollout"]) => rollout != null && rollout !== 100;

const divergence = <T extends string | boolean>(value: T, released: T): T | null =>
  value === released ? null : value;

export function EditReleaseDialog({
  appName,
  deploymentName,
  release,
  autoHalted,
  open,
  onClose,
}: EditReleaseDialogProps) {
  const { t } = useTranslation();
  const updateRelease = useUpdateRelease(appName, deploymentName);
  const fieldErrorPrefix = useId();
  const rolloutHintId = useId();
  const haltCautionId = useId();
  const haltOverrideId = useId();
  const haltRefreshId = useId();
  const historyRefreshing =
    useIsFetching({ queryKey: deploymentsKeys.history(appName, deploymentName) }) > 0;

  const [descriptionEdit, setDescriptionEdit] = useState<string | null>(null);
  const [appVersionEdit, setAppVersionEdit] = useState<string | null>(null);
  const [isMandatoryEdit, setIsMandatoryEdit] = useState<boolean | null>(null);
  const [isDisabledEdit, setIsDisabledEdit] = useState<boolean | null>(null);
  const [rolloutEdit, setRolloutEdit] = useState<string | null>(null);
  const [haltRefused, setHaltRefused] = useState(false);
  const [refusalAnnounced, setRefusalAnnounced] = useState(false);

  const releasedDescription = release.description ?? "";
  const releasedAppVersion = release.appVersion ?? "";
  const releasedIsMandatory = release.isMandatory ?? false;
  const releasedIsDisabled = release.isDisabled ?? false;
  const releasedRollout = release.rollout != null ? String(release.rollout) : "";

  const description = descriptionEdit ?? releasedDescription;
  const appVersion = appVersionEdit ?? releasedAppVersion;
  const isMandatory = isMandatoryEdit ?? releasedIsMandatory;
  const isDisabled = isDisabledEdit ?? releasedIsDisabled;
  const rollout = rolloutEdit ?? releasedRollout;

  const fieldError = isReleaseHalted(updateRelease.error) ? null : updateRelease.error;
  const showHaltCaution = autoHalted || haltRefused;
  const awaitsOverride = showHaltCaution && releasedIsDisabled;
  const confirmsOverride = awaitsOverride && !isDisabled;
  const haltRefreshPending = haltRefused && !releasedIsDisabled;
  const saveDescribedBy = confirmsOverride
    ? haltOverrideId
    : haltRefreshPending
      ? haltRefreshId
      : undefined;
  const rolloutEditable = isUnfinishedRollout(release.rollout);
  const descriptionInvalid = apiFieldHasError(fieldError, "description");
  const appVersionInvalid = apiFieldHasError(fieldError, "appVersion");
  const isMandatoryInvalid = apiFieldHasError(fieldError, "isMandatory");
  const isDisabledInvalid = apiFieldHasError(fieldError, "isDisabled");
  const rolloutInvalid = apiFieldHasError(fieldError, "rollout");

  const buildChanges = (): PackageInfo => {
    const info: PackageInfo = { label: release.label };
    if (description.trim() !== releasedDescription) info.description = description.trim();
    if (appVersion.trim() && appVersion.trim() !== releasedAppVersion)
      info.appVersion = appVersion.trim();
    if (isMandatory !== releasedIsMandatory) info.isMandatory = isMandatory;
    if (isDisabled !== releasedIsDisabled) info.isDisabled = isDisabled;
    if (rolloutEditable && rollout.trim() !== "" && Number(rollout) !== release.rollout)
      info.rollout = Number(rollout);
    return info;
  };

  const changes = buildChanges();
  const hasChanges = Object.keys(changes).length > 1;

  const clearSaveOutcome = () => {
    if (updateRelease.error) updateRelease.reset();
    setRefusalAnnounced(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (updateRelease.isPending) return;
    if (!hasChanges) return;

    setRefusalAnnounced(false);
    updateRelease.mutate(changes, {
      onSuccess: () => onClose(),
      onError: (error) => {
        if (!isReleaseHalted(error)) return;
        setHaltRefused(true);
        setRefusalAnnounced(true);
      },
    });
  };

  const handleClose = () => {
    if (!updateRelease.isPending) {
      updateRelease.reset();
      onClose();
    }
  };

  const inputClass =
    "h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo hover:border-border-light";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`${t("deployments.editRelease")} ${release.label ?? ""}`}
      describedBy={showHaltCaution ? haltCautionId : undefined}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="release-description" className="text-sm font-medium text-subtle">
            {t("deployments.descriptionLabel")}
          </label>
          <textarea
            id="release-description"
            value={description}
            onChange={(e) => {
              setDescriptionEdit(divergence(e.target.value, releasedDescription));
              clearSaveOutcome();
            }}
            rows={3}
            readOnly={updateRelease.isPending}
            aria-disabled={updateRelease.isPending || undefined}
            aria-invalid={descriptionInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(fieldError, "description", fieldErrorPrefix)}
            className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="release-app-version" className="text-sm font-medium text-subtle">
            {t("deployments.targetVersionLabel")}
          </label>
          <input
            id="release-app-version"
            type="text"
            value={appVersion}
            onChange={(e) => {
              setAppVersionEdit(divergence(e.target.value, releasedAppVersion));
              clearSaveOutcome();
            }}
            readOnly={updateRelease.isPending}
            aria-disabled={updateRelease.isPending || undefined}
            aria-invalid={appVersionInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(fieldError, "appVersion", fieldErrorPrefix)}
            className={`${inputClass} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
          />
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-heading">
            <input
              id="release-mandatory"
              type="checkbox"
              checked={isMandatory}
              onChange={(e) => {
                setIsMandatoryEdit(divergence(e.target.checked, releasedIsMandatory));
                clearSaveOutcome();
              }}
              disabled={updateRelease.isPending}
              aria-invalid={isMandatoryInvalid ? true : undefined}
              aria-describedby={apiFieldDescribedBy(fieldError, "isMandatory", fieldErrorPrefix)}
              className="h-4 w-4 accent-indigo"
            />
            {t("deployments.mandatory")}
          </label>
          <label className="flex items-center gap-2 text-sm text-heading">
            <input
              id="release-disabled"
              type="checkbox"
              checked={isDisabled}
              onChange={(e) => {
                setIsDisabledEdit(divergence(e.target.checked, releasedIsDisabled));
                clearSaveOutcome();
              }}
              disabled={updateRelease.isPending}
              aria-invalid={isDisabledInvalid ? true : undefined}
              aria-describedby={apiFieldDescribedBy(
                fieldError,
                "isDisabled",
                fieldErrorPrefix,
                awaitsOverride ? haltOverrideId : undefined,
              )}
              className="h-4 w-4 accent-indigo"
            />
            {t("deployments.disabled")}
          </label>
        </div>

        {rolloutEditable && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="release-rollout" className="text-sm font-medium text-subtle">
              {t("deployments.rolloutLabel")}
            </label>
            <input
              id="release-rollout"
              type="number"
              min={release.rollout ?? 0}
              max={100}
              value={rollout}
              onChange={(e) => {
                setRolloutEdit(divergence(e.target.value, releasedRollout));
                clearSaveOutcome();
              }}
              readOnly={updateRelease.isPending}
              aria-disabled={updateRelease.isPending || undefined}
              aria-invalid={rolloutInvalid ? true : undefined}
              aria-describedby={apiFieldDescribedBy(
                fieldError,
                "rollout",
                fieldErrorPrefix,
                rolloutHintId,
              )}
              className={`${inputClass} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
            />
            <p id={rolloutHintId} className="text-xs text-muted">
              {t("deployments.rolloutHint")}
            </p>
          </div>
        )}

        {showHaltCaution && (
          <Alert variant="warning" announce={false}>
            <p id={haltCautionId}>
              {haltRefused ? t("deployments.releaseHalted") : t("deployments.releaseHaltedOnOpen")}
            </p>
            {awaitsOverride && (
              <p id={haltOverrideId} className="mt-1">
                {confirmsOverride
                  ? t("deployments.releaseHaltedOverrideConfirm")
                  : haltRefused
                    ? t("deployments.releaseHaltedOverride")
                    : t("deployments.releaseHaltedOnOpenOverride")}
              </p>
            )}
            {haltRefreshPending && (
              <p id={haltRefreshId} className="mt-1">
                {historyRefreshing
                  ? t("deployments.releaseHaltedChecking")
                  : t("deployments.releaseHaltedNotRefreshed")}
              </p>
            )}
          </Alert>
        )}
        <p role="status" className="sr-only">
          {refusalAnnounced ? t("deployments.releaseHalted") : ""}
        </p>

        <ApiFieldErrors
          error={fieldError}
          fieldLabels={{
            label: t("deployments.releaseLabelField"),
            appVersion: t("deployments.targetVersionLabel"),
            description: t("deployments.descriptionLabel"),
            rollout: t("deployments.rolloutLabel"),
            isMandatory: t("deployments.mandatory"),
            isDisabled: t("deployments.disabled"),
          }}
          idPrefix={fieldErrorPrefix}
        />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={updateRelease.isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!updateRelease.isPending && !hasChanges}
            aria-disabled={updateRelease.isPending || undefined}
            aria-busy={updateRelease.isPending || undefined}
            data-autofocus={updateRelease.isPending ? true : undefined}
            aria-describedby={saveDescribedBy}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {updateRelease.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
